import { Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowRight } from 'lucide-react';
import { Projet } from '../../types/api';

interface ProjectCardProps {
  project: Projet;
  showProgress?: boolean;
  className?: string;
}

export default function ProjectCard({ project, showProgress = false, className = '' }: ProjectCardProps) {
  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatPercentage = (percentage: number) => {
    return typeof percentage === 'number' 
      ? percentage.toFixed(2) 
      : percentage;
  };

  const calculateDuration = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(project.date_fin_prevue);
    endDate.setHours(0, 0, 0, 0);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffMonths / 12);
    const remainingMonths = diffMonths % 12;

    if (diffYears > 0 && remainingMonths > 0) {
      return `Livré en ${diffYears} an${diffYears > 1 ? 's' : ''} ${remainingMonths} mois`;
    } else if (diffYears > 0) {
      return `Livré en ${diffYears} an${diffYears > 1 ? 's' : ''}`;
    } else if (diffMonths > 0) {
      return `Livré en ${diffMonths} mois`;
    } else if (diffDays > 0) {
      return `Livré en ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else {
      return 'Livré';
    }
  };

  return (
    <Link 
      to={`/projects/${project.id}`}
      className={`group block bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden ${className}`}
    >
      {/* Image */}
      <div className="relative h-64">
        <img
          src={project.image_principale_url || 'https://demo.sogeimco.com/static/images/default-project.svg'}
          alt={project.nom_projet}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.currentTarget.src = 'https://demo.sogeimco.com/static/images/default-project.svg';
          }}
        />
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            project.statut_projet === 'EN_COURS' ? 'bg-green-100 text-green-800' :
            project.statut_projet === 'A_VENIR' ? 'bg-blue-100 text-blue-800' :
            project.statut_projet === 'TERMINE' ? 'bg-gray-100 text-gray-800' :
            'bg-red-100 text-red-800'
          }`}>
            {project.statut_projet === 'EN_COURS' ? 'En cours' :
             project.statut_projet === 'A_VENIR' ? 'À venir' :
             project.statut_projet === 'TERMINE' ? 'Terminé' :
             'Suspendu'}
          </span>
        </div>

        {/* Progress Bar */}
        {showProgress && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Avancement</span>
              <span className="text-sm font-medium text-blue-400">
                {formatPercentage(project.pourcentage_avancement)}%
              </span>
            </div>
            <div className="w-full bg-gray-600 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${formatPercentage(project.pourcentage_avancement)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
          {project.nom_projet}
        </h3>
        
        <p className="text-gray-600 mb-4 line-clamp-2">
          {project.description}
        </p>

        {/* Project Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center text-gray-600">
            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
            <span className="text-sm">{project.adresse}</span>
          </div>
          
          <div className="flex items-center text-gray-600">
            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
            <span className="text-sm">
              {calculateDuration()}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold text-blue-600">
            À partir de {' '}
            {formatCurrency(project.mensualite_minimale || project.apport_initial_minimal || 150000)}
          </div>
          <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
            <span className="text-sm mr-1">VOIR DETAIL</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}
